import {
  UseQueryOptions,
  useIsFetching,
  useIsMutating,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import React from 'react';

import { bootstrapConfig, globalConfig } from '~src/context/config';

import { Api, Session, StateResponse } from './../api/api';
import { useIpc } from './utils/ipc-utils';

const { userId, externalAddressBookProvider } = bootstrapConfig;

export const axiosConfig: AxiosRequestConfig = {
  headers: {
    Authorization: `Bearer ${bootstrapConfig.token}`,
    'ingrid-session-id': bootstrapConfig.sessionId,
  },
  baseURL: globalConfig.ingridApiAddr,
};

export const apiInstance = new Api(axiosConfig);

apiInstance.instance.interceptors.request.use(
  async config => {
    if (config.headers) {
      config.headers['x-external-address-book-provider'] = externalAddressBookProvider || '';
      if (userId) {
        // in case of external_address_book_provider we don't send user id in headers
        config.headers['x-ingrid-user-id'] = userId;
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

export function useGlobalStateQuery(
  options: Omit<UseQueryOptions<StateResponse, AxiosError>, 'queryKey'> = {},
  queryFnSideEffect?: (state: StateResponse) => void
) {
  const ipc = useIpc();

  return useQuery<StateResponse, AxiosError>({
    queryKey: ['state'],
    queryFn: async () => {
      try {
        const { data } = await apiInstance.v4.stateGet(bootstrapConfig.sessionId);
        queryFnSideEffect?.(data);

        return data;
      } catch (e) {
        if (
          axios.isAxiosError(e) &&
          e.response?.status === 401 &&
          (e as AxiosError<{ code: 'unauthorized' }>).response?.data?.code === 'unauthorized'
        ) {
          ipc.send({
            name: 'error',
            data: {
              type: 'missing_session_token',
              severity: 'fatal',
            },
          });
        }

        throw e;
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (
        bootstrapConfig.stateGetRetry &&
        (error.response?.status === 409 || `${error.response?.status}`.startsWith('5')) &&
        failureCount < 4
      ) {
        return true;
      }
      return false;
    },
    retryDelay: i => i * 150,
    ...options,
  });
}

export function useGlobalStateUpdate() {
  const queryClient = useQueryClient();
  const ipc = useIpc();
  return (data: StateResponse | undefined) => {
    const oldData = queryClient.getQueryData(['state']) as StateResponse;

    if (data && data.sessions[0].flags.send_state_events && oldData.version! > data.version!) {
      return;
    }

    if (data) {
      /**
       * It is our top priority to sync the state to fullscreen iframe before
       * we do any kind of rendering below. In extreme case the widget can
       * start with a dialog open and will otherwise crash because of state
       * undefined. It has to be sent here, since react-query doesn't treat
       * implicit setQueryData calls as network requests since v4.
       */
      ipc.send({
        name: 'sync_state',
        data,
      });
    }
    return queryClient.setQueryData(['state'], data);
  };
}

/**
 * Check if we should show global loader - we do it in situations where
 * optimistic update is not possible or impractical:
 *
 * 1. address - shipping categories can change on new address
 * 2. customer number - we have to wait for backend to apply customer number
 *    before we allow user select the category.
 * 3. delivery addon - it is rarely used and difficult to do optimistic update
 *    so we do not care.
 */
export function useIsHardMutating() {
  const isAddressMutating = useIsMutating({ mutationKey: ['address'] });
  const isPurchaseCountryMutating = useIsMutating({ mutationKey: ['purchase-country'] });
  const isCustomerNumberMutating = useIsMutating({ mutationKey: ['customer-number'] });
  const isDeliveryAddonMutating = useIsMutating({ mutationKey: ['delivery-addon'] });
  const isDeliveryAddressFetching = useIsFetching({ queryKey: ['form'] });
  const isBillingAddressMutating = useIsMutating({ mutationKey: ['billing-address'] });
  const isConsolidationMutating = useIsMutating({ mutationKey: ['consolidation'] });

  return (
    isAddressMutating ||
    isPurchaseCountryMutating ||
    isCustomerNumberMutating ||
    isDeliveryAddonMutating ||
    isDeliveryAddressFetching ||
    isBillingAddressMutating ||
    isConsolidationMutating
  );
}

/*
 * TODO: gradually replace global state with session state.
 */
export const SessionStateContext = React.createContext<Session | undefined>(undefined);

export const GlobalStateContext = React.createContext<StateResponse | undefined>(undefined);

// Only specific session data
export function useSessionState() {
  const session = React.useContext(SessionStateContext);
  if (session !== undefined) {
    return session;
  }
  throw 'SessionStateContext is not yet defined, you are probably using it in a wrong place';
}

// Exposes whole BE response
export function useGlobalState() {
  const globalState = React.useContext(GlobalStateContext);
  if (globalState !== undefined) {
    return globalState;
  }
  throw 'GlobalStateContext is not yet defined, you are probably using it in a wrong place';
}

export const GlobalStateMaster: React.FC<React.PropsWithChildren> = ({ children }) => {
  const updateGlobalState = useGlobalStateUpdate();
  const ipc = useIpc();

  const stateQuery = useGlobalStateQuery({}, state => {
    /**
     * It is our top priority to sync the state to fullscreen iframe before
     * we do any kind of rendering below. In extreme case the widget can
     * start with a dialog open and will otherwise crash because of state
     * undefined. It need to be set via additional callback, since react-query
     * has removed onSuccess method from their API.
     */
    ipc.send({
      name: 'sync_state',
      data: state,
    });
  });

  React.useEffect(() => {
    /**
     * In location search we do api calls from fullscreen, so it is possible
     * that we update the state from there and sync it right back.
     */
    ipc.listen(payload => {
      if (payload.name === 'sync_state') {
        updateGlobalState(payload.data);
      }
    });
  }, []);

  return (
    <GlobalStateContext.Provider value={stateQuery.data!}>{children}</GlobalStateContext.Provider>
  );
};

export const GlobalStateMirror: React.FC<React.PropsWithChildren> = ({ children }) => {
  const ipc = useIpc();

  const [globalState, setGlobalState] = React.useState<StateResponse | undefined>(undefined);

  React.useEffect(() => {
    ipc.listen(payload => {
      if (payload.name === 'sync_state') {
        setGlobalState(payload.data);
      }
    });
  }, []);

  return <GlobalStateContext.Provider value={globalState}>{children}</GlobalStateContext.Provider>;
};
